import { Box, Typography } from '@mui/material';
import { useChildTokens } from './useChildTokens';
import { formatTime, remainingFraction, TimerViewProps, TIMER_SURFACE } from './timerView';

/**
 * Tårtbiten — återstående tid som en bit på en urtavla (Time-Timer-principen).
 * Den visar ALLTID återstående tid, aldrig klockslag: biten försvinner när tiden
 * är ute. Därför visar alla fyra visningsformerna samma värde och är utbytbara.
 *
 * RIKTNING (som en fysisk Time Timer): biten ligger MOTURS från 12 och krymper
 * mot 12 — dess rörliga kant vandrar 6 → 9 → 12. conic-gradient mäter alltid
 * medsols från 12, så biten läggs i intervallet [360 - kvar, 360] och det som är
 * förbrukat lämnas genomskinligt först. Att i stället lägga färgen i [0, kvar]
 * ger en kil som sveper 6 → 3 → 12, alltså spegelvänt mot en Time Timer.
 *
 * Ytan är ringens (TIMER_SURFACE) och färgerna kommer ur barntemats tokens. Ren
 * renderare: tiden kommer som prop, ingen egen setInterval (se timerView.ts).
 *
 * Hette tidigare TimerDial. Namnet flyttade till formen med VISARE när formerna blev
 * fritt kombinerbara — det här är tårtbiten, inte urtavlan med visare.
 */

const TICK_COUNT = 12;
/** Knappens ram. CssBaseline sätter box-sizing: border-box, så absolut
 *  positionerade barn utgår från PADDING-boxen (TIMER_SURFACE - 2 * ramen) —
 *  strecken måste rotera kring dess centrum, inte kring ytans. */
const FACE_BORDER = 2;
const TICK_INSET = 3;
const FACE_RADIUS = TIMER_SURFACE / 2;
/** Rotationscentrum inuti ett timstreck: padding-boxens centrum minus strecket
 *  eget avstånd från kanten. */
const TICK_ORIGIN_Y = FACE_RADIUS - FACE_BORDER - TICK_INSET;
const INNER_DISC = 118;

const TimerWedge = ({
  time,
  totalSeconds,
  label,
  color,
  onToggle,
  showDigits = true,
}: TimerViewProps) => {
  const tokens = useChildTokens();
  const degrees = remainingFraction(time, totalSeconds) * 360;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2.5 }}>
      <Box
        component="button"
        type="button"
        data-testid="timer-wedge"
        onClick={onToggle}
        aria-label={label}
        sx={{
          width: TIMER_SURFACE,
          height: TIMER_SURFACE,
          borderRadius: '50%',
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          p: 0,
          bgcolor: tokens.card,
          border: `${FACE_BORDER}px solid`,
          borderColor: tokens.border,
          fontFamily: 'inherit',
          overflow: 'hidden',
          cursor: onToggle ? 'pointer' : 'default',
        }}
      >
        {/* Tårtbiten = tiden som är kvar. Den läggs moturs från 12 (se
            RIKTNING i filens dokkommentar). */}
        <Box
          data-testid="timer-wedge-fill"
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: `conic-gradient(transparent 0deg ${360 - degrees}deg, ${color} ${
              360 - degrees
            }deg)`,
            transition: 'background 1s linear',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />

        {/* Timstreck — gör tavlan läsbar som en klocka utan att röra sig. */}
        {Array.from({ length: TICK_COUNT }, (_unused, i) => {
          const isQuarter = i % 3 === 0;
          return (
            <Box
              key={i}
              aria-hidden
              sx={{
                position: 'absolute',
                top: TICK_INSET,
                left: '50%',
                width: isQuarter ? 3 : 2,
                height: isQuarter ? 13 : 8,
                ml: isQuarter ? '-1.5px' : '-1px',
                borderRadius: '2px',
                bgcolor: isQuarter ? tokens.text : tokens.textSoft,
                transformOrigin: `50% ${TICK_ORIGIN_Y}px`,
                transform: `rotate(${i * (360 / TICK_COUNT)}deg)`,
              }}
            />
          );
        })}

        <Box
          sx={{
            position: 'relative',
            width: INNER_DISC,
            height: INNER_DISC,
            borderRadius: '50%',
            bgcolor: tokens.card,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 0.5,
          }}
        >
          {showDigits && (
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: tokens.text }}>
              {formatTime(time)}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: '0.68rem',
              fontWeight: 700,
              color: tokens.textSoft,
              textAlign: 'center',
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TimerWedge;
